<template>
  <div class="space-y-4">
    <article v-for="line in lines" :key="line.id_ligne_origine" class="rounded-lg border border-gray-200 overflow-hidden">
      <p v-if="line.id_commande" class="border-b bg-gray-50 px-4 py-2 text-sm font-semibold">Commande {{ line.id_commande_groupe }}<template v-if="line.nom_boutique"> · {{ line.nom_boutique }}</template></p>
      <div class="grid sm:grid-cols-2">
        <div v-for="side in sides(line)" :key="side.key" class="p-4 space-y-3" :class="side.key === 'origine' ? 'bg-red-50' : unavailable(line) ? 'bg-gray-50' : status(line) === 'pending' ? 'bg-indigo-50' : 'bg-green-50'">
          <p class="mb-2"><span class="replacement-state inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold uppercase" :class="side.key === 'origine' ? 'replacement-state--rupture' : unavailable(line) ? 'replacement-state--unavailable' : status(line) === 'pending' ? 'replacement-state--proposed' : 'replacement-state--confirmed'">{{ side.label }}</span></p>
          <p v-if="side.key === 'remplacement' && unavailable(line)" class="text-sm">Article maintenu en rupture.</p>
          <div v-for="product in side.products" :key="product.id_produit" class="flex gap-3">
            <img v-if="product.image_url" :src="product.image_url" alt="" class="w-20 h-24 object-contain rounded bg-white" referrerpolicy="no-referrer" @error="hideImage" />
            <div class="min-w-0 text-sm space-y-1 break-words">
              <p class="font-semibold break-words">{{ product.reference }}</p>
              <p>{{ product.couleur }}</p>
              <p>{{ sizes(product) }}</p>
              <p><template v-if="side.key === 'origine' && product.vendu_par !== 'tailles'">{{ product.quantite_pack }} paquet(s) · </template>{{ quantity(product) }} pièce(s)</p>
              <p>{{ replacementMoney(product.montant_ht) }} HT · {{ replacementMoney(product.montant_ttc) }} TTC</p>
              <div v-if="editable && side.key === 'remplacement' && status(line) === 'pending'" class="flex flex-wrap gap-3 pt-2">
                <button type="button" class="text-indigo-700 underline" @click="$emit('edit', line, product, 'quantity')">Quantité</button>
                <button type="button" class="text-red-700 underline" @click="$emit('edit', line, product, 'remove')">Annuler ce candidat</button>
                <button type="button" class="text-indigo-700 underline" @click="$emit('edit', line, product, 'product')">Changer de produit</button>
              </div>
              <template v-if="side.key === 'remplacement' && status(line) === 'pending'">
                <p v-if="product.charge_vendeur_ttc" class="text-amber-800">Charge vendeur si choisi : {{ replacementMoney(product.charge_vendeur_ttc) }} TTC</p>
                <p v-else-if="product.a_rembourser_ttc" class="text-blue-700">Remboursement estimé : {{ replacementMoney(product.a_rembourser_ttc) }} TTC (hors remise fixe)</p>
              </template>
            </div>
          </div>
        </div>
      </div>
      <div v-if="status(line) === 'pending' ? displayProducts(line).length > 1 : line.charge_vendeur_ttc > 0 || line.a_rembourser_ttc > 0" class="p-3 text-sm bg-white space-y-1">
        <template v-if="status(line) === 'pending'">
          <p>{{ displayProducts(line).length }} produits · 1 au choix</p>
          <p v-if="line.charge_vendeur_max_ttc" class="text-amber-800">Charge vendeur (max.) : {{ replacementMoney(line.charge_vendeur_max_ttc) }} TTC</p>
        </template>
        <template v-else>
          <p v-if="line.charge_vendeur_ttc > 0" class="text-amber-800">Charge vendeur : {{ replacementMoney(line.charge_vendeur_ttc) }} TTC</p>
          <p v-if="line.a_rembourser_ttc > 0" class="text-blue-700">{{ provisional ? 'Remboursement estimé' : 'Remboursement prévu' }} : <strong>{{ replacementMoney(line.a_rembourser_ttc) }} TTC</strong></p>
        </template>
      </div>
    </article>
  </div>
</template>
<script setup lang="ts">
import type { ReplacementAppliedProduct, ReplacementLine, ReplacementSnapshot } from '@/api/remplacements';
import { replacementMoney, replacementLineProducts } from '@/api/remplacements';
defineProps<{ lines: ReplacementLine[]; provisional?: boolean; editable?: boolean }>();
defineEmits<{ edit: [line: ReplacementLine, product: ReplacementAppliedProduct, action: 'quantity' | 'remove' | 'product'] }>();
function status(line: ReplacementLine) { return line.statut || (line.id_ligne_remplacement != null ? 'accepted' : 'pending'); }
function unavailable(line: ReplacementLine) { return ['rejected', 'expired', 'cancelled'].includes(status(line)); }
function displayProducts(line: ReplacementLine) {
  const products = replacementLineProducts(line);
  if (unavailable(line)) return [];
  return products;
}
function sides(line: ReplacementLine) { return [
  { key: 'origine', label: 'En rupture', products: [line.origine as ReplacementAppliedProduct] },
  { key: 'remplacement', label: status(line) === 'pending' ? 'Remplacements proposés' : status(line) === 'expired' ? 'Proposition expirée' : status(line) === 'cancelled' ? 'Proposition annulée' : status(line) === 'rejected' ? 'Remplacement refusé' : 'Remplacement confirmé', products: displayProducts(line) },
]; }
function sizes(product: ReplacementSnapshot) {
  return product.quantites.map((quantity, index) => quantity ? `${product.tailles[index] || 'Assortiment'} × ${quantity}` : '').filter(Boolean).join(' · ');
}
function quantity(product: ReplacementSnapshot) { return product.quantites.reduce((sum, q) => sum + q, 0); }
function hideImage(event: Event) { (event.target as HTMLImageElement).style.display = 'none'; }
</script>

<style scoped>
.replacement-state--rupture {
  background-color: #fee2e2;
  color: #991b1b;
}

.replacement-state--proposed {
  background-color: #e0e7ff;
  color: #3730a3;
}

.replacement-state--confirmed {
  background-color: #d1fae5;
  color: #065f46;
}

.replacement-state--unavailable {
  background-color: #e5e7eb;
  color: #374151;
}
</style>
